<template>
    <card icon="newspaper"
          :headline="$t('facebook.ads.headline', { status: statusText })"
          :description="$t('facebook.ads.description')"
    >
        <div slot="header" class="btn-filters" v-if="hasWebshopData">
            <a v-for="filter in filters.filters"
               :class="{'btn-info': filters.current === filter.option}"
               class="btn btn-sm"
               @click.prevent="switchMetrics(filter.option)"
               v-tooltip="$t(`facebook.ads.filters.${filter.name}`)">
                <fa :icon="filter.icon"/>
            </a>
        </div>
        <div class="table-wrapper table-selectable">
            <facebook-ads-table-header :status="status"/>

            <div class="table-body" v-if="!hasLoaded">
                <facebook-ads-table-row v-for="n in !selectedCampaign ? limit(status) : 5"
                                        :status="status"
                                        :campaign="n"
                                        :key="n"/>
            </div>

            <template v-if="!selectedCampaign">
                <div class="table-body">
                    <facebook-ads-table-row v-if="hasLoaded"
                                            v-for="campaign in campaignsWithInsights"
                                            :status="status"
                                            type="campaign"
                                            :campaign="campaign"
                                            :key="campaign.id"/>

                </div>
            </template>

            <ad-sets v-else-if="!selectedAdSet" :status="status" @hasLoaded="setHasLoaded"/>

            <ads v-else @hasLoaded="setHasLoaded" :status="status"/>
        </div>


        <pagination :total="total"
                    :limit="limit(status)"
                    :page="page"
                    v-if="total > limit(status) && !selectedCampaign"
                    @paginate="paginate"
                    :showFirstAndLast="false"
                    size="small"
        />
    </card>
</template>

<script>
  import FacebookAdsTableRow from '@/app/facebook/components/ads/FacebookAdsTableRow'
  import FacebookAdsTableHeader from '@/app/facebook/components/ads/FacebookAdsTableHeader'
  import AdSets from '@/app/facebook/components/ads/AdSets'
  import Pagination from '@/app/layout/components/paginator/Pagination'
  import Ads from '@/app/facebook/components/ads/Ads'
  import { mapGetters } from 'vuex'
  import datepickerMixin from '@/app/datepicker/datepicker.mixin'

  export default {
    mixins: [datepickerMixin],
    data () {
      return {
        isLoading: false,
        campaigns: [],
        hasLoaded: false,
        total: 0,
        page: 1,
        pagination: {
          next: null,
          previous: null
        }
      }
    },

    props: {
      status: {
        type: String,
        required: true
      }
    },

    computed: {
      ...mapGetters('facebookAds', {
        limit: 'getLimit',
        metrics: 'getMetrics',
        selected: 'getSelected',
        filters: 'getFilters',
        hasWebshopData: 'hasWebshopData'
      }),

      selectedCampaign () {return this.selected(this.status).campaign},

      selectedAdSet () {return this.selected(this.status).adSet},

      selectedAd () {return this.selected(this.status).ad},

      statusText () {return $tc(`facebook.ads.${this.status.toLowerCase()}`, 10)},

      totalPages () {return Math.ceil(this.total / this.limit(this.status))},

      campaignsWithInsights () {
        return this.campaigns.filter(campaign => {
          return campaign.status === this.status
        }).sort((a, b) => {
          if (!a.insights || !b.insights) {
            return 0
          }
          let aAsInteger = parseInt(a.insights.ctr)
          let bAsInteger = parseInt(b.insights.ctr)
          if (aAsInteger < bAsInteger) {
            return 1
          }
          return -1
        })
      }
    },

    watch: {
      metrics () {this.load()}
    },

    mounted () {this.load()},

    methods: {
      setHasLoaded (value) {
        this.hasLoaded = value
      },

      next () {
        this.page >= this.totalPages ? (this.page = 1) : this.page++
        this.load({ token: this.pagination.next })
      },

      previous () {
        this.page <= 1 ? (this.page = this.totalPages) : this.page--
        this.load({ token: this.pagination.previous })
      },

      async load (paginate) {
          this.hasLoaded = false

          const payload = {
            status: this.status
          }

          if (paginate && paginate.token) {
            payload.token = paginate.token
          }

          await this.$store.dispatch('facebookAds/resetSelected', { status: this.status })

          const response = await this.$store.dispatch('facebookAds/loadCampaigns', payload)

          const { previous, next, campaigns, total } = response

          this.pagination.previous = previous
          this.pagination.next = next
          this.campaigns = campaigns
          this.total = total

          this.$emit('loaded', {campaigns, status: this.status})

          this.hasLoaded = true
      },

      paginate (page) {
        page > this.page ? this.load({ token: this.pagination.next }) : this.load({ token: this.pagination.previous })
        this.page = page
      },

      switchMetrics (option) {
        if (option !== this.filters.current) {
          this.$store.dispatch('facebookAds/changeFilter', option)
        }
      }
    },
    components: {
      FacebookAdsTableHeader,
      FacebookAdsTableRow,
      Pagination,
      AdSets,
      Ads
    }
  }
</script>

<style lang="scss">
    @import '~@/assets/scss/_table.scss';

    .btn-filters {
        .btn-info {
            background: #5192ce;
            border-color: #5192ce;
            color: white;
        }

        .btn:first-child {
            margin-right: 5px;
        }
    }
</style>
